import {
  ESP32BLEBluetooth,
  ESP32Options,
  ESP32OTAUpdateOptions,
  heartbeat,
  readData,
  writeData,
} from "@/services/domainControl";
import { HEARTBEAT } from "@/services/domainControl/define";
import { platform } from "@/utils/capacitor";
import { BluetoothPluginDevice } from "@/utils/capacitor/bluetoothPlugin";
import {
  buildSignalObject,
  parseSignalObject,
} from "@/views/DomainControl/helper/signalTransform";
import { numberToUUID } from "@capacitor-community/bluetooth-le";
import { defineStore, acceptHMRUpdate } from "pinia";
import { Notify } from "quasar";

type BluetoothDeviceLocal = BluetoothDevice | BluetoothPluginDevice;

const getDefaultOutputValue = () =>
  ({
    UITx_DisableBrk_FL: 0,
    UITx_DisableBrk_FR: 0,
    UITx_CoStab: 20,
    UITx_DisableBrk_RL: 0,
    UITx_DisableBrk_RR: 0,
    UITx_CoStaticChar: 20,
    UITx_TAA_ModeSelect: 0,
    UITx_CoDynChar: 20,
    UITx_CST_Ena: 0,
    UITx_DST_Ena: 1,
    UITx_TAA_Ena: 0,
    UITx_DMC_Ena: 1,
    UITx_ActuatorEna_SbW: 0,
    UITx_ActuatorEna_Rws: 0,
    UITx_ActuatorEna_FAS: 0,
    UITx_ActuatorEna_DTD: 0,
    UITx_SbTv_Ena: 0,
    UITx_RollingCounter: 0,
    UITx_VLMReset: 0,
    UITx_FrontVgrSw: 2,
    UITx_AdaDrvrCtrlSt: 0,
    UITx_SbTvRwsEna: 0,
    UITx_DURrst: 0,
    not_use_1: 0,
    not_use_2: 0,
  } as any as DomianControl.OutputValue);
const getDefaultOutputValueParsed = () =>
  ({
    UITx_ActuatorEna_Rws: 0,
  } as any as DomianControl.OutputValue);

export const useDomainControlStore = defineStore({
  id: "domainControl",
  state: () => ({
    isConnected: false,
    outputValue: getDefaultOutputValue(), // outputValue 原始值 未经 parseSignalObject
    outputValueParsed: getDefaultOutputValueParsed(), // outputValue 转换后
    printLog: false,
    printInterval: false,
    heartbeatTimer: null as any,
    notifyInterval: null as any as number,
    connectStartTime: null as any as number,
    device: null as unknown as BluetoothDeviceLocal,
    isAndroidMainUser: true,
    showVConsole: localStorage.getItem("showVConsole") === "true" || false,
  }),
  getters: {},
  actions: {
    async connectToDevice(
      handleDisconnected: any,
      loading: any,
      type = "ESP32Update"
    ) {
      loading.value[0] = true;
      try {
        const options =
          type === "ESP32Update" ? ESP32OTAUpdateOptions : ESP32Options;
        console.log("[BOWEN_LOG] 🚀 ~~ connectToDevice ~~ options:", options);

        const newDevice = await ESP32BLEBluetooth.request(options);
        console.log(
          "[BOWEN_LOG] 🚀 ~~ connectToDevice ~~ newDevice:",
          newDevice
        );
        this.device = newDevice as unknown as BluetoothDeviceLocal;
        await ESP32BLEBluetooth.connect(handleDisconnected);
        this.changeIsConnectedStatus(true);
      } catch (error) {
        console.error("连接设备失败:", error);
        loading.value[0] = false;
        throw error;
      }
      loading.value[0] = false;
    },
    async disconnectDevice() {
      if (ESP32BLEBluetooth.device) {
        ESP32BLEBluetooth.disconnect();
        this.changeIsConnectedStatus(false);
        console.log("[BOWEN_LOG] 🚀 ~ disconnectDevice ~ disconnected");
      }
    },
    // 热更新重连
    async pageMounted(handleDisconnected: any) {
      if (ESP32BLEBluetooth.device && ESP32BLEBluetooth.isConnected) {
        this.changeIsConnectedStatus(true);
        if (!(ESP32BLEBluetooth.device as BluetoothDevice)?.gatt?.connected) {
          await ESP32BLEBluetooth.connect(handleDisconnected);
        }
      }
    },
    changeIsConnectedStatus(status: boolean) {
      if (status) {
        // this.startIrregularHeartbeat();
        this.connectStartTime = Date.now();
      } else {
        this.connectStartTime = null as any;
        this.device = null as unknown as BluetoothDeviceLocal;
      }
      this.isConnected = status;
    },
    changePrintLogStatus(status: boolean) {
      this.printLog = status;
    },
    changePrintIntervalStatus(status: boolean) {
      this.printInterval = status;
    },
    changeNotifyInterval(status: number) {
      this.notifyInterval = status;
    },
    changeIsAndroidMainUser(status: boolean) {
      this.isAndroidMainUser = status;
    },
    changeShowVConsole(status: boolean) {
      this.showVConsole = status;
      localStorage.setItem("showVConsole", status.toString());
    },
    async readOutputValue() {
      let value = await readData();
      if (value) {
        this.outputValue = value; // 原始值
        value = parseSignalObject(value, true) as DomianControl.OutputValue; // 转换后
        this.outputValueParsed = value;
      }
      console.log("[BOWEN_LOG] 🚀 ~~ readOutputValue ~~ value:", value);
      return value;
    },
    async writeOutputValue(params: DomianControl.OutputParams) {
      // if(!this.isAndroidMainUser && platform === "android") {
      //   return;
      // }
      // 转为为原始值
      const newParams = buildSignalObject(params, true);
      const pre = typeof this.outputValue === "object" ? this.outputValue : {};
      // 合并数据
      const data = {
        ...getDefaultOutputValue(),
        ...pre,
        ...newParams,
      };
      // 写入数据
      await writeData(data);
      // 更新 outputValue
      Object.keys(data).map((key) => {
        this.outputValue[key] = data[key];
      });

      // 更新 outputValueParsed
      const dataParsed = {
        ...(this.outputValueParsed || {}),
        ...params,
      };
      Object.keys(dataParsed).map((key) => {
        this.outputValueParsed[key] = dataParsed[key];
      });
    },
    async startIrregularHeartbeat() {
      clearInterval(this.heartbeatTimer);
      // const uuid = numberToUUID(HEARTBEAT.uuid);
      // const characteristics = await ESP32BLEBluetooth.getCharacteristics();
      // const hascharacteristic = characteristics?.find(
      //   (item) => item.uuid === uuid
      // );
      // console.log(
      //   "[BOWEN_LOG] 🚀 ~~ startIrregularHeartbeat ~~ hascharacteristic:",
      //   hascharacteristic
      // );
      // if (!hascharacteristic) return;
      this.heartbeatTimer = setInterval(() => {
        heartbeat("heartbeat");
      }, 1000);
    },
    stopIrregularHeartbeat() {
      clearInterval(this.heartbeatTimer);
    },
    sentryTest() {
      const randomString = Math.random().toString(36).substring(2, 15);
      throw new Error("sentry test" + randomString + new Date().getTime());
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useDomainControlStore, import.meta.hot)
  );
}
